import React, { useEffect, useMemo, useState } from "react";
import Page from "pages/Page";
import { useTheme } from "@/components/ThemeProvider";
import { fetchUserActivity, type UserReport, fetchAskAWatchChatsActivity } from "@/api/userMetrics";

export default function UserMetrics() {
  const [activeTab, setActiveTab] = useState<"activity" | "dashboard">("activity");
  const { variant } = useTheme();

  // user email (could come from auth context; hard-coded default for now)
  const [userEmail, setUserEmail] = useState("jdoe@corp");
  const [reports, setReports] = useState<UserReport[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // NEW: AskAWatch chats
  const [chatLoading, setChatLoading] = useState(false);
  const [chatError, setChatError] = useState<string | null>(null);
  const [chatReports, setChatReports] = useState<
    { timestamps: string; thread_id: string; user_message: string; user_email: string; example_prompt: string | null; prompt_category: string | null; }[]
  >([]);

  const [fromDt, setFromDt] = useState<string>(""); // datetime-local value
  const [toDt, setToDt] = useState<string>("");

  const buttonBase =
    "px-4 py-2 rounded-t-lg font-medium transition-colors border border-border/50 focus:outline-none text-xs";
  const activeStyle =
    variant === "light"
      ? "bg-[#003F2D] text-white border-[#18E78F]/50"
      : "bg-[#003F2D]/80 text-slate-200 border-[#18E78F]/50";
  const inactiveStyle =
    variant === "light"
      ? "bg-white text-[#003F2D] hover:bg-[#18E78F]/10 border-border/50"
      : "bg-slate-900 text-slate-200 hover:bg-[#18E78F]/20 border-border/50";

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    console.log('Fetching user activity with fromDt:', fromDt, 'toDt:', toDt);
    fetchUserActivity({ start: fromDt || undefined, end: toDt || undefined })
      .then(d => !cancelled && setReports(d.reports || []))
      .catch(e => !cancelled && setError(e.message || "Failed to load reports"))
      .finally(() => !cancelled && setLoading(false));
    return () => { cancelled = true; };
  }, [fromDt, toDt]);

  useEffect(() => {
    let cancelled = false;
    setChatLoading(true);
    setChatError(null);
    fetchAskAWatchChatsActivity({ start: fromDt || undefined, end: toDt || undefined })
      .then(d => !cancelled && setChatReports(d.reports || []))
      .catch(e => !cancelled && setChatError(e.message || "Failed to load AskAWatch chats"))
      .finally(() => !cancelled && setChatLoading(false));
    return () => { cancelled = true; };
  }, [fromDt, toDt]);

  // helper to format a Date to input[type=datetime-local] value (local tz, no seconds)
  function toLocalInputValue(d: Date) {
    const pad = (n: number) => String(n).padStart(2, "0");
    return `${d.getFullYear()}-${pad(d.getMonth()+1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
  }

  // DEFAULT: on mount set range to past 2 days ending now (location-based: local timezone)
  useEffect(() => {
    if (!fromDt && !toDt) {
      const now = new Date();               // local user timezone
      const endISO = toLocalInputValue(now);
      const start = new Date(now);
      start.setDate(start.getDate() - 10);   // past 2 days
      const startISO = toLocalInputValue(start);
      setFromDt(startISO);
      setToDt(endISO);
    }
  }, []); // run once

  // Quick preset handler (optional)
  const applyPreset = (preset: "1h"|"2h"|"4h"|"1d"|"2d"|"1w"|"1mo") => {
    const now = new Date();                 // local user timezone
    const endISO = toLocalInputValue(now);
    const start = new Date(now);
    const minutesMap: Record<string, number> = {
      "1h": 60, "2h": 120, "4h": 240,
      "1d": 1440, "2d": 2880,
      "1w": 10080, "1mo": 43200
    };
    start.setMinutes(start.getMinutes() - minutesMap[preset]);
    const startISO = toLocalInputValue(start);
    setFromDt(startISO);
    setToDt(endISO);
  };

  const metrics = useMemo(() => {
    const healthChecks = reports.filter(r => r.type?.toLowerCase().includes("health")).length;
    const changeValidations = reports.filter(r => r.type?.toLowerCase().includes("change")).length;
    const uniqueConfigClasses = new Set(reports.map(r => r.configuration_class)).size;
    return {
      total: reports.length,
      healthChecks,
      changeValidations,
      uniqueConfigClasses,
      uniqueReporters: new Set(reports.map(r => r.report_by)).size
    };
  }, [reports]);

  // NEW: chat insights
  const chatInsights = useMemo(() => {
    const totalChats = chatReports.length;
    const uniqueChatUsers = new Set(chatReports.map(c => c.user_email)).size;
    const categories = Array.from(new Set(chatReports.map(c => c.prompt_category).filter(Boolean))) as string[];
    const latestExamplePrompt = chatReports.find(c => c.example_prompt)?.example_prompt || null;

    // Top category counts
    const catCounts = chatReports.reduce<Record<string, number>>((acc, c) => {
      const k = c.prompt_category || "Uncategorized";
      acc[k] = (acc[k] || 0) + 1;
      return acc;
    }, {});
    const topCategories = Object.entries(catCounts)
      .sort((a, b) => b[1] - a[1])
      .slice(0, 4);

    return { totalChats, uniqueChatUsers, categories, latestExamplePrompt, topCategories };
  }, [chatReports]);

  // Derived chat distributions
  const chatDaily = useMemo(() => {
    const map = new Map<string, number>();
    chatReports.forEach(c => {
      const d = new Date(c.timestamps);
      if (!isNaN(d.getTime())) {
        const key = d.toISOString().slice(0, 10);
        map.set(key, (map.get(key) || 0) + 1);
      }
    });
    // Last 10 days sorted ascending
    return Array.from(map.entries())
      .sort((a,b) => a[0].localeCompare(b[0]))
      .slice(-10);
  }, [chatReports]);

  const chatByHour = useMemo(() => {
    const arr = Array(24).fill(0);
    chatReports.forEach(c => {
      const d = new Date(c.timestamps);
      if (!isNaN(d.getTime())) {
        arr[d.getHours()]++;
      }
    });
    const max = Math.max(1, ...arr);
    return { hours: arr, max };
  }, [chatReports]);

  const topChatUsers = useMemo(() => {
    const counts: Record<string, number> = {};
    chatReports.forEach(c => {
      counts[c.user_email] = (counts[c.user_email] || 0) + 1;
    });
    return Object.entries(counts)
      .sort((a,b) => b[1]-a[1])
      .slice(0,5);
  }, [chatReports]);

  // NEW: top users for change validation reports
  const topChangeUsers = useMemo(() => {
    const counts: Record<string, number> = {};
    reports
      .filter(r => r.type?.toLowerCase().includes("change"))
      .forEach(r => {
        counts[r.report_by] = (counts[r.report_by] || 0) + 1;
      });
    return Object.entries(counts)
      .sort((a,b) => b[1]-a[1])
      .slice(0,5);
  }, [reports]);

  // NEW: top health check users
  const topHealthUsers = useMemo(() => {
    const counts: Record<string, number> = {};
    reports
      .filter(r => r.type?.toLowerCase().includes("health"))
      .forEach(r => {
        counts[r.report_by] = (counts[r.report_by] || 0) + 1;
      });
    return Object.entries(counts).sort((a,b) => b[1] - a[1]).slice(0,5);
  }, [reports]);

  // Users per day (unique reporters)
  const usersPerDay = useMemo(() => {
    const byDay = new Map<string, Set<string>>();
    reports.forEach(r => {
      const d = new Date(r.updated_at);
      if (!isNaN(d.getTime())) {
        const day = d.toISOString().slice(0,10);
        if (!byDay.has(day)) byDay.set(day, new Set());
        byDay.get(day)!.add(r.report_by);
      }
    });
    return Array.from(byDay.entries())
      .map(([day,set]) => [day, set.size] as [string, number])
      .sort((a,b) => a[0].localeCompare(b[0]))
      .slice(-14); // last 14 days
  }, [reports]);

  // Reports generated per day (all types)
  const reportsPerDay = useMemo(() => {
    const byDay = new Map<string, number>();
    reports.forEach(r => {
      const d = new Date(r.updated_at);
      if (!isNaN(d.getTime())) {
        const day = d.toISOString().slice(0,10);
        byDay.set(day, (byDay.get(day) || 0) + 1);
      }
    });
    return Array.from(byDay.entries())
      .sort((a,b) => a[0].localeCompare(b[0]))
      .slice(-14);
  }, [reports]);

  // Health check reports generated per day
  const healthReportsPerDay = useMemo(() => {
    const byDay = new Map<string, number>();
    reports
      .filter(r => r.type?.toLowerCase().includes("health"))
      .forEach(r => {
        const d = new Date(r.updated_at);
        if (!isNaN(d.getTime())) {
          const day = d.toISOString().slice(0,10);
          byDay.set(day, (byDay.get(day) || 0) + 1);
        }
      });
    return Array.from(byDay.entries()).sort((a,b) => a[0].localeCompare(b[0])).slice(-14);
  }, [reports]);

  // Chats vs date (daily chat counts)
  const chatsPerDay = useMemo(() => {
    const byDay = new Map<string, number>();
    chatReports.forEach(c => {
      const d = new Date(c.timestamps);
      if (!isNaN(d.getTime())) {
        const day = d.toISOString().slice(0,10);
        byDay.set(day, (byDay.get(day) || 0) + 1);
      }
    });
    return Array.from(byDay.entries()).sort((a,b) => a[0].localeCompare(b[0])).slice(-14);
  }, [chatReports]);

  // AskAWatch chat users vs date (unique chat users per day)
  const chatUsersPerDay = useMemo(() => {
    const byDay = new Map<string, Set<string>>();
    chatReports.forEach(c => {
      const d = new Date(c.timestamps);
      if (!isNaN(d.getTime())) {
        const day = d.toISOString().slice(0,10);
        if (!byDay.has(day)) byDay.set(day, new Set());
        byDay.get(day)!.add(c.user_email);
      }
    });
    return Array.from(byDay.entries())
      .map(([day,set]) => [day, set.size] as [string, number])
      .sort((a,b) => a[0].localeCompare(b[0]))
      .slice(-14);
  }, [chatReports]);

  // Reports generated per day (change validation only)
  const changeReportsPerDay = useMemo(() => {
    const byDay = new Map<string, number>();
    reports
      .filter(r => r.type?.toLowerCase().includes("change"))
      .forEach(r => {
        const d = new Date(r.updated_at);
        if (!isNaN(d.getTime())) {
          const day = d.toISOString().slice(0,10);
          byDay.set(day, (byDay.get(day) || 0) + 1);
        }
      });
    return Array.from(byDay.entries()).sort((a,b) => a[0].localeCompare(b[0])).slice(-14);
  }, [reports]);

  const timeAgo = (iso: string) => {
    if (!iso) return "-";
    const dt = new Date(iso);
    if (isNaN(dt.getTime())) return iso;
    const diff = Date.now() - dt.getTime();
    const m = Math.floor(diff / 60000);
    if (m < 1) return "just now";
    if (m < 60) return `${m}m ago`;
    const h = Math.floor(m / 60);
    if (h < 24) return `${h}h ago`;
    return `${Math.floor(h / 24)}d ago`;
  };

  // parse storage_url that may look like "['u1','u2']" or "u1,u2"
  const parseUrls = (raw: string | null | undefined): string[] => {
    if (!raw) return [];
    const s = raw.trim();
    // Try JSON-like list
    if (s.startsWith("[") && s.endsWith("]")) {
      try {
        const arr = JSON.parse(s.replace(/'/g, '"'));
        return Array.isArray(arr) ? arr.map((x: unknown) => String(x).trim()).filter(Boolean) : [];
      } catch {
        // fall through to comma split
      }
    }
    return s.split(",").map(x => x.trim()).filter(Boolean);
  };

  // Show actual date/time in Central time (or local if you prefer)
  const formatDateTime = (iso: string) => {
    if (!iso) return "-";
    const dt = new Date(iso);
    if (isNaN(dt.getTime())) return iso;
    // Local timezone format; adjust options as needed
    return dt.toLocaleString(undefined, {
      year: "numeric",
      month: "short",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit"
    });
  };

  // --- Derive top lists for the three sections ---

  // 1) AskAWatch chat users: email -> unique session count (unique thread_id)
  const chatSessionsByUser = useMemo(() => {
    const map = new Map<string, Set<string>>();
    chatReports.forEach(c => {
      const email = c.user_email || "unknown";
      const tid = c.thread_id || `${c.user_email}-${c.timestamps}`;
      if (!map.has(email)) map.set(email, new Set());
      map.get(email)!.add(tid);
    });
    // return sorted [email, sessionCount]
    return Array.from(map.entries())
      .map(([email, set]) => [email, set.size] as [string, number])
      .sort((a,b) => b[1] - a[1]);
  }, [chatReports]);

  // 2) Change validation: email -> count of change validation reports
  const changeReportsByUser = useMemo(() => {
    const counts: Record<string, number> = {};
    reports
      .filter(r => r.type?.toLowerCase().includes("change"))
      .forEach(r => {
        const email = r.report_by || "unknown";
        counts[email] = (counts[email] || 0) + 1;
      });
    return Object.entries(counts).sort((a,b) => b[1] - a[1]);
  }, [reports]);

  // 3) Health check: email -> count of health check reports
  const healthReportsByUser = useMemo(() => {
    const counts: Record<string, number> = {};
    reports
      .filter(r => r.type?.toLowerCase().includes("health"))
      .forEach(r => {
        const email = r.report_by || "unknown";
        counts[email] = (counts[email] || 0) + 1;
      });
    return Object.entries(counts).sort((a,b) => b[1] - a[1]);
  }, [reports]);

  return (
    <Page >
      <div className="flex flex-col w-full h-full bg-accent dark:bg-card border border-border/50 rounded-none">
        {/* Sticky header with centered tabs */}
        <div className="sticky top-0 z-10 bg-accent dark:bg-card border-b border-border/50 px-6 pt-3 pb-3">
          {/* Header: tabs left, filters right */}
          <div className="flex items-center gap-3">
            {/* LEFT: tabs */}
            <div className="flex justify-start gap-2">
              <button
                className={`${buttonBase} ${activeTab === "activity" ? activeStyle : inactiveStyle}`}
                onClick={() => setActiveTab("activity")}
              >
                User Activity
              </button>
              <button
                className={`${buttonBase} ${activeTab === "dashboard" ? activeStyle : inactiveStyle}`}
                onClick={() => setActiveTab("dashboard")}
              >
                Dashboard
              </button>
            </div>

            {/* RIGHT: date range */}
            <div className="ml-auto flex items-center gap-2">
              <label className="text-[10px] text-muted-foreground">From</label>
              <input
                type="datetime-local"
                value={fromDt}
                onChange={e => setFromDt(e.target.value)}
                className="text-[12px] px-2 py-1 border border-border/50 rounded bg-background/70 dark:bg-background/40"
              />
              <label className="text-[10px] text-muted-foreground">To</label>
              <input
                type="datetime-local"
                value={toDt}
                onChange={e => setToDt(e.target.value)}
                className="text-[12px] px-2 py-1 border border-border/50 rounded bg-background/70 dark:bg-background/40"
              />
              {(fromDt || toDt) && (
                <button
                  onClick={() => { setFromDt(""); setToDt(""); }}
                  className="px-2 py-1 text-[11px] rounded border border-border/50 bg-background/60 hover:bg-red-500/20"
                >
                  Clear
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="p-6 flex-1 overflow-y-auto">
          {activeTab === "activity" && (
            <div className="flex flex-col gap-2">
              {loading && <div className="text-xs text-muted-foreground text-center py-2">Loading…</div>}
              {error && <div className="text-xs text-red-500 text-center py-2">{error}</div>}

              {/* Minimal activity lines (use reports from API directly) */}
              {!loading && reports.map(r => {
                const isHealth = r.type?.toLowerCase().includes("health");
                const isChange = r.type?.toLowerCase().includes("change");
                const typeLabel = isHealth ? "health check" : isChange ? "change validation" : (r.type || "report");
                const urls = parseUrls(r.storage_url);

                return (
                  <div key={r.id} className="flex flex-wrap items-center gap-1.5 text-[12px] bg-background/50 dark:bg-background/30 border border-border/40 rounded-md px-3 py-2">
                    <span className="font-medium">{r.report_by}</span>
                    <span>created</span>
                    <span className="px-1.5 py-0.5 rounded bg-[#003F2D]/20 text-[#003F2D] dark:text-slate-200 border border-border/40 font-medium">{typeLabel}</span>
                    <span>report for</span>
                    <span className="px-1.5 py-0.5 rounded bg-[#003F2D]/20 text-[#003F2D] dark:text-slate-200 border border-border/40 font-medium">{r.configuration_class || "-"}</span>
                    <span>at</span>
                    <span className="text-muted-foreground">{formatDateTime(r.updated_at)}</span>
                    {urls.length > 0 && <span className="text-muted-foreground">•</span>}
                    {urls.length > 0 &&
                      urls.map((u, i) => (
                        <a key={`${r.id}-${i}`} href={u} target="_blank" rel="noopener noreferrer" className="underline text-[#18E78F]">
                          {`Report ${i + 1}`}
                        </a>
                      )).reduce<React.ReactNode[]>((acc, el, i, arr) => {
                        acc.push(el); if (i < arr.length - 1) acc.push(<span key={`sep-${r.id}-${i}`}>,</span>); return acc;
                      }, [])
                    }
                  </div>
                );
              })}

              {!loading && reports.length === 0 && !error && (
                <div className="text-xs text-muted-foreground/70 py-4 text-center">No reports found.</div>
              )}
            </div>
          )}

          {activeTab === "dashboard" && (
            <div className="flex flex-col gap-6">
              {/* Stats (optional) */}
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                <Stat label="AskAWatch Chats" value={String(chatReports.length)} />
                <Stat label="Chat Users" value={String(new Set(chatReports.map(c => c.user_email)).size)} />
                <Stat label="Reports (Change/Health)" value={`${reports.filter(r=>r.type?.toLowerCase().includes("change")).length}/${reports.filter(r=>r.type?.toLowerCase().includes("health")).length}`} />
              </div>

              {/* ONLY 4 report widgets below */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* 1) AskAWatch chat users vs date */}
                <div className="bg-background/50 dark:bg-background/40 border border-border/40 rounded-lg px-3 py-2">
                  <h3 className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground mb-2">
                    AskAWatch Chat Users vs Date
                  </h3>
                  {chatUsersPerDay.length === 0 ? (
                    <div className="text-[11px] text-muted-foreground">No chat users.</div>
                  ) : (
                    <div className="w-full h-40">
                      <ChartBars data={chatUsersPerDay} color="#18E78F" />
                    </div>
                  )}
                </div>

                {/* 2) AskAWatch chats vs date */}
                <div className="bg-background/50 dark:bg-background/40 border border-border/40 rounded-lg px-3 py-2">
                  <h3 className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground mb-2">
                    AskAWatch Chats vs Date
                  </h3>
                  {chatsPerDay.length === 0 ? (
                    <div className="text-[11px] text-muted-foreground">No chats.</div>
                  ) : (
                    <div className="w-full h-40">
                      <ChartBars data={chatsPerDay} color="#18E78F" />
                    </div>
                  )}
                </div>

                {/* 3) Change validation reports generated vs date */}
                <div className="bg-background/50 dark:bg-background/40 border border-border/40 rounded-lg px-3 py-2">
                  <h3 className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground mb-2">
                    Change Validation Reports vs Date
                  </h3>
                  {changeReportsPerDay.length === 0 ? (
                    <div className="text-[11px] text-muted-foreground">No change validation reports.</div>
                  ) : (
                    <div className="w-full h-40">
                      <ChartBars data={changeReportsPerDay} color="#18E78F" />
                    </div>
                  )}
                </div>

                {/* 4) Health check reports generated vs date */}
                <div className="bg-background/50 dark:bg-background/40 border border-border/40 rounded-lg px-3 py-2">
                  <h3 className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground mb-2">
                    Health Check Reports vs Date
                  </h3>
                  {healthReportsPerDay.length === 0 ? (
                    <div className="text-[11px] text-muted-foreground">No health checks.</div>
                  ) : (
                    <div className="w-full h-40">
                      <ChartBars data={healthReportsPerDay} color="#18E78F" />
                    </div>
                  )}
                </div>
              </div>

              {/* NEW: Three sections listing users and counts */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {/* A) AskAWatch chat users - sessions per email */}
                <div className="bg-background/50 dark:bg-background/40 border border-border/40 rounded-lg px-3 py-2">
                  <h3 className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground mb-2">
                    AskAWatch Chat Sessions by User
                  </h3>
                  {chatSessionsByUser.length === 0 ? (
                    <div className="text-[11px] text-muted-foreground">No chat sessions.</div>
                  ) : (
                    <ul className="flex flex-col gap-1">
                      {chatSessionsByUser.map(([email, sessions]) => {
                        const initials = email.split("@")[0].split(/[._-]/).map(s => s[0]).join("").slice(0,2).toUpperCase();
                        return (
                          <li key={email} className="flex items-center gap-2 text-[11px]">
                            <div className="w-6 h-6 rounded-full bg-[#003F2D] text-white flex items-center justify-center text-[10px] border border-[#18E78F]/40">
                              {initials}
                            </div>
                            <span className="flex-1 truncate">{email}</span>
                            <span className="text-muted-foreground">{sessions}</span>
                          </li>
                        );
                      })}
                    </ul>
                  )}
                </div>

                {/* B) Change validation reports by user */}
                <div className="bg-background/50 dark:bg-background/40 border border-border/40 rounded-lg px-3 py-2">
                  <h3 className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground mb-2">
                    Change Validation Reports by User
                  </h3>
                  {changeReportsByUser.length === 0 ? (
                    <div className="text-[11px] text-muted-foreground">No change validation reports.</div>
                  ) : (
                    <ul className="flex flex-col gap-1">
                      {changeReportsByUser.map(([email, count]) => {
                        const initials = email.split("@")[0].split(/[._-]/).map(s => s[0]).join("").slice(0,2).toUpperCase();
                        return (
                          <li key={email} className="flex items-center gap-2 text-[11px]">
                            <div className="w-6 h-6 rounded-full bg-indigo-700 text-white flex items-center justify-center text-[10px] border border-[#18E78F]/40">
                              {initials}
                            </div>
                            <span className="flex-1 truncate">{email}</span>
                            <span className="text-muted-foreground">{count}</span>
                          </li>
                        );
                      })}
                    </ul>
                  )}
                </div>

                {/* C) Health check reports by user */}
                <div className="bg-background/50 dark:bg-background/40 border border-border/40 rounded-lg px-3 py-2">
                  <h3 className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground mb-2">
                    Health Check Reports by User
                  </h3>
                  {healthReportsByUser.length === 0 ? (
                    <div className="text-[11px] text-muted-foreground">No health checks.</div>
                  ) : (
                    <ul className="flex flex-col gap-1">
                      {healthReportsByUser.map(([email, count]) => {
                        const initials = email.split("@")[0].split(/[._-]/).map(s => s[0]).join("").slice(0,2).toUpperCase();
                        return (
                          <li key={email} className="flex items-center gap-2 text-[11px]">
                            <div className="w-6 h-6 rounded-full bg-emerald-700 text-white flex items-center justify-center text-[10px] border border-[#18E78F]/40">
                              {initials}
                            </div>
                            <span className="flex-1 truncate">{email}</span>
                            <span className="text-muted-foreground">{count}</span>
                          </li>
                        );
                      })}
                    </ul>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </Page>
  );
}

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex flex-col bg-background/50 dark:bg-background/40 border border-border/40 rounded-lg px-2 py-1.5">
      <span className="text-[9px] uppercase tracking-wide text-muted-foreground">{label}</span>
      <span className="text-[11px] font-semibold">{value}</span>
    </div>
  );
}

// Simple reusable bar chart (date:value pairs)
function ChartBars({ data, color }: { data: [string, number][], color: string }) {
  const barWidth = 18;
  const barGap = 8;
  const leftPad = 16;           // add slight left padding so first x-axis label is visible
  const rightPad = 8;
  const W = Math.max(data.length * (barWidth + barGap) + leftPad + rightPad, 360);
  const H = 160;

  const max = Math.max(...data.map(([, v]) => v), 1);
  const axisY = H - 18;
  const scaleY = (v: number) => Math.round(((v / max) * (H - 32)));

  return (
    <div className="w-full h-full">
      <svg
        viewBox={`0 0 ${W} ${H}`}
        preserveAspectRatio="xMinYMin meet"
        className="w-full h-full"
      >
        {/* baseline axis (start after leftPad) */}
        <line x1={leftPad} y1={axisY} x2={W - rightPad} y2={axisY} stroke="currentColor" strokeOpacity="0.2" />
        {data.map(([day, v], i) => {
          const x = leftPad + i * (barWidth + barGap);
          const h = scaleY(v);
          const y = axisY - h;
          return (
            <g key={day}>
              <rect x={x} y={y} width={barWidth} height={h} rx="3" fill={color} opacity="0.9" />
              <text x={x + barWidth / 2} y={y - 4} fontSize="10" textAnchor="middle" fill={color}>
                {v}
              </text>
              <text x={x + barWidth / 2} y={H - 4} fontSize="9" textAnchor="middle" fill="currentColor" opacity="0.7">
                {day.slice(5)}
              </text>
            </g>
          );
        })}
      </svg>
    </div>
  );
}